import { useCallback, useEffect, useRef } from "react";
import {
  paintSelection,
  rangeForOffsets,
  ReaderSelection,
  readerSelectionFromOffsets,
  wordRangeAtPoint,
  WordRange,
} from "@/lib/selection";

interface Options {
  // Selection is wired up only while enabled (e.g. not while the sheet is open).
  enabled: boolean;
  // True only while narration is actively playing (listening AND not paused): a
  // plain tap then jumps playback instead of selecting. Pausing flips this off,
  // so tapping returns to selecting words — pause is the "inspect text" switch.
  // Long-press always selects regardless.
  isPlaying: () => boolean;
  // A finalised selection, ready to hand to the WordingSheet.
  onSelect: (sel: ReaderSelection) => void;
  // A plain tap on a paragraph while narration plays — jump narration to it.
  // Fired straight from the touch/click handler (not a trailing synthetic
  // click) so it stays reliable on mobile.
  onTapWhilePlaying?: (segmentId: string) => void;
}

// Hold this long on a word (without scrolling away) to start a selection; from
// there a drag extends it word by word — the touch equivalent of click-drag.
const LONG_PRESS_MS = 400;
// Finger travel that still counts as a "still" press rather than a scroll.
const MOVE_SLOP = 10;

/**
 * Re-implements reader text selection on top of point hit-testing, so we can
 * keep the browser's native selection suppressed (user-select: none) — that's
 * what stops mobile Chrome/Safari from popping their own selection toolbar and
 * the contextual-search sheet on top of our rewording sheet.
 *
 * Touch: tap a word to select it; long-press then drag to extend across words.
 * Mouse: press-drag-release (or a plain click) selects directly.
 * The highlight is painted via the CSS Custom Highlight API.
 */
export function useReaderSelection({
  enabled,
  isPlaying,
  onSelect,
  onTapWhilePlaying,
}: Options) {
  // Stays true from the moment a drag-select begins through the synthetic click
  // that trails touchend, so the Pager skips the page-swipe and the paragraph
  // onClick skips the narration jump for that one gesture. Reset on the next
  // press (and by clear()).
  const wasSelectingRef = useRef(false);
  // Live props read from the (re)bound listeners without re-running the effect.
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onTapRef = useRef(onTapWhilePlaying);
  onTapRef.current = onTapWhilePlaying;

  useEffect(() => {
    if (!enabled) return;

    let anchor: WordRange | null = null; // word where the gesture began
    let current: { start: number; end: number } | null = null; // combined range
    let startPoint: { x: number; y: number } | null = null;
    let mode: "idle" | "pending" | "selecting" = "idle";
    let timer: number | null = null;

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    // Merge the anchor word with the word under (x, y) into one [start, end)
    // range inside the anchor's paragraph, and paint it. Points that leave the
    // anchor paragraph keep the range pinned to the anchor word.
    const extendTo = (x: number, y: number) => {
      if (!anchor) return null;
      const w = wordRangeAtPoint(x, y);
      const same = w && w.para === anchor.para;
      const start = same ? Math.min(anchor.start, w.start) : anchor.start;
      const end = same ? Math.max(anchor.end, w.end) : anchor.end;
      paintSelection(rangeForOffsets(anchor.para, start, end));
      return { start, end };
    };

    const beginSelecting = () => {
      if (!anchor) return;
      mode = "selecting";
      wasSelectingRef.current = true;
      current = { start: anchor.start, end: anchor.end };
      paintSelection(rangeForOffsets(anchor.para, anchor.start, anchor.end));
    };

    const finish = () => {
      if (!anchor || !current) return;
      const sel = readerSelectionFromOffsets(
        anchor.para,
        current.start,
        current.end,
      );
      if (sel) onSelectRef.current(sel);
    };

    const reset = () => {
      clearTimer();
      anchor = null;
      current = null;
      startPoint = null;
      mode = "idle";
    };

    // ---- touch ----
    const onTouchStart = (e: TouchEvent) => {
      reset();
      wasSelectingRef.current = false;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      const w = wordRangeAtPoint(t.clientX, t.clientY);
      if (!w) return; // not on reader text → leave scrolling / swiping alone
      anchor = w;
      startPoint = { x: t.clientX, y: t.clientY };
      mode = "pending";
      timer = window.setTimeout(beginSelecting, LONG_PRESS_MS);
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || mode === "idle") return;
      if (mode === "pending") {
        // Moved before the long-press fired → it's a scroll, not a select.
        const moved =
          startPoint &&
          Math.hypot(t.clientX - startPoint.x, t.clientY - startPoint.y) >
            MOVE_SLOP;
        if (moved) reset();
        return;
      }
      // selecting → own the gesture (stop the page scrolling under the finger).
      e.preventDefault();
      current = extendTo(t.clientX, t.clientY) ?? current;
    };

    const onTouchEnd = () => {
      if (mode === "selecting") {
        finish();
      } else if (mode === "pending" && anchor && isPlayingRef.current()) {
        // Quick tap while playing: jump narration to the tapped paragraph.
        const segId = anchor.para.dataset.ttsSeg;
        if (segId) onTapRef.current?.(segId);
      } else if (mode === "pending" && anchor) {
        // Quick tap (paused or not listening): select the tapped word.
        const sel = readerSelectionFromOffsets(
          anchor.para,
          anchor.start,
          anchor.end,
        );
        if (sel) {
          paintSelection(
            rangeForOffsets(anchor.para, anchor.start, anchor.end),
          );
          onSelectRef.current(sel);
        }
      }
      clearTimer();
      anchor = null;
      current = null;
      startPoint = null;
      mode = "idle";
      // wasSelectingRef intentionally left as-is until the next press.
    };

    // ---- mouse (desktop): press-drag-release / click selects directly ----
    const onMouseDown = (e: MouseEvent) => {
      reset();
      wasSelectingRef.current = false;
      if (e.button !== 0) return;
      const w = wordRangeAtPoint(e.clientX, e.clientY);
      if (!w) return;
      anchor = w;
      startPoint = { x: e.clientX, y: e.clientY };
      beginSelecting();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (mode !== "selecting" || e.buttons === 0) return;
      current = extendTo(e.clientX, e.clientY) ?? current;
    };

    const onMouseUp = (e: MouseEvent) => {
      if (mode !== "selecting" || !anchor || !current) return;
      const moved =
        startPoint &&
        Math.hypot(e.clientX - startPoint.x, e.clientY - startPoint.y) >
          MOVE_SLOP;
      // A click with no drag, while narration plays, jumps narration instead.
      if (!moved && isPlayingRef.current()) {
        paintSelection(null);
        wasSelectingRef.current = false;
        const segId = anchor.para.dataset.ttsSeg;
        if (segId) onTapRef.current?.(segId);
      } else {
        finish();
      }
      anchor = null;
      current = null;
      startPoint = null;
      mode = "idle";
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      clearTimer();
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [enabled]);

  // Drop the current highlight (e.g. when the sheet closes or the page turns).
  const clear = useCallback(() => {
    wasSelectingRef.current = false;
    paintSelection(null);
  }, []);
  // True while/just after the active gesture is a selection — callers use this
  // to suppress a page-swipe or narration jump for that gesture.
  const isSelecting = useCallback(() => wasSelectingRef.current, []);

  return { clear, isSelecting };
}
